import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Lock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { InvoiceLineItems } from './InvoiceLineItems';
import { useUpdateInvoice, useInvoice } from '@/hooks/useInvoices';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { resolveTaxConfig, taxFromNet, formatTaxLine } from '@/utils/tax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { InvoiceLineItem } from '@/types/invoice';
import { round2 } from '@/utils/partsPricing';

interface EditInvoiceDialogProps {
    invoiceId: string | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function EditInvoiceDialog({ invoiceId, open, onOpenChange }: EditInvoiceDialogProps) {
    const { data: invoice, isLoading } = useInvoice(invoiceId);
    const updateInvoice = useUpdateInvoice();
    const liveCompanyTax = useCompanyTax();
    const { formatMoney } = useCompanyLocale();

    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [issueDate, setIssueDate] = useState<Date>(new Date());
    const [dueDate, setDueDate] = useState<Date>(new Date());
    const [notes, setNotes] = useState('');
    const [terms, setTerms] = useState('');
    const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);

    // Populate fields when invoice data loads
    useEffect(() => {
        if (invoice) {
            setCustomerName(invoice.customer_name || '');
            setCustomerEmail(invoice.customer_email || '');
            setCustomerPhone(invoice.customer_phone || '');
            setCustomerAddress(invoice.customer_address || '');
            setNotes(invoice.notes || '');
            setTerms(invoice.terms || '');
            setLineItems(
                Array.isArray(invoice.line_items) && invoice.line_items.length > 0
                    ? invoice.line_items
                    : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }]
            );
            if (invoice.issue_date) {
                try { setIssueDate(parseISO(invoice.issue_date)); } catch { setIssueDate(new Date()); }
            }
            if (invoice.due_date) {
                try { setDueDate(parseISO(invoice.due_date)); } catch { setDueDate(new Date()); }
            }
        }
    }, [invoice]);

    const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxCfg = resolveTaxConfig(invoice, liveCompanyTax);
    const gstAmount = taxFromNet(subtotal, taxCfg);
    const totalAmount = subtotal + gstAmount;

    // Settled money: a paid or refunded invoice can have its breakdown corrected
    // (wrong description, split a line, move an amount between lines) but the
    // total the customer paid must come out the same. Save is blocked otherwise.
    const totalLocked = invoice?.status === 'paid' || invoice?.status === 'refunded';
    // Pin to what the customer actually paid. Some tech-app invoices were
    // stored a cent under the amount collected (ex-tax rounding), so a paid
    // invoice printed "Balance due -$0.01". Within a cent, the paid figure is
    // the truth; the DB lock allows exactly this correction.
    const storedTotal = Number(invoice?.total_amount ?? 0);
    const paidAmount = Number(invoice?.amount_paid ?? 0);
    const lockedTotal =
        totalLocked && paidAmount > 0 && Math.abs(storedTotal - paidAmount) <= 0.011 ? paidAmount : storedTotal;
    // Compare against the same calculation run over the original lines rather than
    // the stored total: close_job rounds per line, so the stored figure can sit a
    // cent off the recomputed one before anyone touches the form. The stored total
    // itself is written back unchanged on save.
    const originalSubtotal = (Array.isArray(invoice?.line_items) ? invoice.line_items : [])
        .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const baselineTotal = originalSubtotal + taxFromNet(originalSubtotal, taxCfg);
    const totalMismatch = totalLocked && Math.abs(totalAmount - baselineTotal) > 0.011;
    const totalDelta = totalAmount - baselineTotal;

    const handleSubmit = async () => {
        if (!invoiceId || !customerName || totalMismatch) return;

        // Stamp bill-to edits so contact sync from the job / customer card can
        // never overwrite them (a customer may want their company name and no
        // address on the invoice). See migration 20260916110000.
        const billToChanged =
            customerName !== (invoice?.customer_name || '') ||
            customerEmail !== (invoice?.customer_email || '') ||
            customerPhone !== (invoice?.customer_phone || '') ||
            customerAddress !== (invoice?.customer_address || '');

        await updateInvoice.mutateAsync({
            id: invoiceId,
            customer_name: customerName,
            customer_email: customerEmail || null,
            customer_phone: customerPhone || null,
            customer_address: customerAddress || null,
            ...(billToChanged ? { bill_to_edited_at: new Date().toISOString() } : {}),
            issue_date: format(issueDate, 'yyyy-MM-dd'),
            due_date: format(dueDate, 'yyyy-MM-dd'),
            notes: notes || null,
            terms: terms || null,
            // Keep $0.00 lines — they list work included in the bulk service price
            line_items: lineItems.filter(item => item.description.trim()),
            // Whole cents only. The money columns have no scale, so an unrounded
            // 219.99499… would be stored as-is and print as a 0.01 balance.
            subtotal: round2(subtotal),
            // On a locked invoice keep the stored total exactly and let the tax
            // line absorb any sub-cent rounding so subtotal + tax still adds up.
            gst_amount: totalLocked ? round2(lockedTotal - round2(subtotal)) : round2(gstAmount),
            total_amount: totalLocked ? lockedTotal : round2(round2(subtotal) + round2(gstAmount)),
        });

        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        Edit Invoice {invoice?.invoice_number ? `— ${invoice.invoice_number}` : ''}
                    </DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">Loading invoice…</div>
                ) : (
                    <div className="space-y-6 py-4">
                        {totalLocked && (
                            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
                                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                                <p>
                                    This invoice is {invoice?.status === 'refunded' ? 'refunded' : 'paid'}. You can change the
                                    line items and details, but the total must stay{' '}
                                    <span className="font-semibold tabular-nums">{formatMoney(lockedTotal)}</span>.
                                    It will not save otherwise.
                                </p>
                            </div>
                        )}

                        {/* Customer Details */}
                        <div className="space-y-4">
                            <h3 className="font-semibold">Customer Details</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-customerName">Customer Name *</Label>
                                    <Input
                                        id="edit-customerName"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        placeholder="Enter customer name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-customerPhone">Phone</Label>
                                    <Input
                                        id="edit-customerPhone"
                                        value={customerPhone}
                                        onChange={(e) => setCustomerPhone(e.target.value)}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-customerEmail">Email</Label>
                                    <Input
                                        id="edit-customerEmail"
                                        type="email"
                                        value={customerEmail}
                                        onChange={(e) => setCustomerEmail(e.target.value)}
                                        placeholder="Enter email address"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-customerAddress">Address</Label>
                                    <Input
                                        id="edit-customerAddress"
                                        value={customerAddress}
                                        onChange={(e) => setCustomerAddress(e.target.value)}
                                        placeholder="Enter billing address"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Invoice Dates */}
                        <div className="space-y-4">
                            <h3 className="font-semibold">Invoice Dates</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Issue Date</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {format(issueDate, 'PPP')}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={issueDate}
                                                onSelect={(date) => date && setIssueDate(date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div className="space-y-2">
                                    <Label>Due Date</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {format(dueDate, 'PPP')}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={dueDate}
                                                onSelect={(date) => date && setDueDate(date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div className="space-y-4">
                            <h3 className="font-semibold">Line Items</h3>
                            <InvoiceLineItems items={lineItems} onChange={setLineItems} />
                        </div>

                        {/* Totals preview */}
                        <div className="rounded-lg border bg-muted/30 p-4 space-y-1 text-sm">
                            <div className="flex justify-between text-muted-foreground">
                                <span>Subtotal</span><span className="tabular-nums">{formatMoney(subtotal)}</span>
                            </div>
                            {taxCfg.enabled && (
                                <div className="flex justify-between text-muted-foreground">
                                    <span>{formatTaxLine(taxCfg)}</span><span className="tabular-nums">{formatMoney(gstAmount)}</span>
                                </div>
                            )}
                            <div className={cn('flex justify-between font-semibold text-base pt-1 border-t', totalMismatch && 'text-destructive')}>
                                <span>Total</span><span className="tabular-nums">{formatMoney(totalAmount)}</span>
                            </div>
                            {totalMismatch && (
                                <p className="pt-1 text-xs font-medium text-destructive">
                                    Must equal {formatMoney(lockedTotal)} on a {invoice?.status} invoice.{' '}
                                    {totalDelta > 0 ? 'Remove' : 'Add'} {formatMoney(Math.abs(totalDelta))} to save.
                                </p>
                            )}
                        </div>

                        {/* Notes & Terms */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-notes">Notes (Internal)</Label>
                                <Textarea
                                    id="edit-notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Internal notes..."
                                    rows={3}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-terms">Payment Terms</Label>
                                <Textarea
                                    id="edit-terms"
                                    value={terms}
                                    onChange={(e) => setTerms(e.target.value)}
                                    placeholder="Payment terms..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!customerName || lineItems.length === 0 || totalMismatch || updateInvoice.isPending || isLoading}
                        title={totalMismatch ? `Total must stay ${formatMoney(lockedTotal)}` : undefined}
                    >
                        {updateInvoice.isPending ? 'Saving…' : 'Save Changes'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
